import React from 'react';
import { Table, Skeleton, Flex } from 'antd';
import type { TableProps } from 'antd';
import { USERS_COLUMN_WIDTHS, USERS_TABLE_SCROLL_X } from '../UsersTable/columnWidths';

interface UsersTableSkeletonProps {
  /** How many placeholder rows to render. */
  rows?: number;
  /** Whether to reserve space for the leading avatar (matches UsersTable.showAvatar). */
  showAvatar?: boolean;
}

type SkeletonRow = { key: string };

/**
 * Loading placeholder for {@link UsersTable}. Renders the SAME Ant Design
 * `Table` shell — identical column widths, header, rounded border and
 * horizontal scroll — so the layout doesn't shift when real data arrives.
 * Each cell mirrors the geometry of its real counterpart:
 *  - User:   optional avatar + name line
 *  - Email:  text line
 *  - Activation: one status chip
 *  - Role:   full-width select-shaped block
 *  - Teams:  two rounded tag pills
 *  - Access: two pill badges, one under the other, + arrow button (matches `.org-access-cell`)
 *  - Status: small switch-shaped block
 */
export function UsersTableSkeleton({ rows = 8, showAvatar = false }: UsersTableSkeletonProps) {
  const dataSource: SkeletonRow[] = Array.from({ length: rows }).map((_, i) => ({
    key: `u-skeleton-${i}`,
  }));

  const columns: TableProps<SkeletonRow>['columns'] = [
    {
      title: ' ',
      key: 'user',
      width: USERS_COLUMN_WIDTHS.user,
      render: () => (
        <Flex align="center" gap={10}>
          {showAvatar && <Skeleton.Avatar active size={32} shape="circle" />}
          <Skeleton.Input active size="small" style={{ width: 110, minWidth: 110, height: 16 }} />
        </Flex>
      ),
    },
    {
      title: ' ',
      key: 'email',
      width: USERS_COLUMN_WIDTHS.email,
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 180, minWidth: 180, height: 16 }} />
      ),
    },
    {
      title: ' ',
      key: 'activation',
      width: USERS_COLUMN_WIDTHS.activation,
      render: () => (
        <Skeleton.Button active size="small" style={{ width: 112, minWidth: 112, height: 24 }} />
      ),
    },
    {
      title: ' ',
      key: 'role',
      width: USERS_COLUMN_WIDTHS.role,
      render: () => (
        <Skeleton.Input active size="small" block style={{ width: '100%', height: 32 }} />
      ),
    },
    {
      title: ' ',
      key: 'teams',
      render: () => (
        <Flex gap={6} wrap="wrap">
          <Skeleton.Button
            active
            size="small"
            shape="round"
            style={{ width: 64, minWidth: 64, height: 24 }}
          />
          <Skeleton.Button
            active
            size="small"
            shape="round"
            style={{ width: 80, minWidth: 80, height: 24 }}
          />
        </Flex>
      ),
    },
    {
      title: ' ',
      key: 'access',
      width: USERS_COLUMN_WIDTHS.access,
      render: () => (
        <Flex align="center" gap={6} wrap="nowrap">
          <Flex vertical gap={6}>
            <Skeleton.Button
              active
              size="small"
              shape="round"
              style={{ width: 96, minWidth: 96, height: 26 }}
            />
            <Skeleton.Button
              active
              size="small"
              shape="round"
              style={{ width: 96, minWidth: 96, height: 26 }}
            />
          </Flex>
          <Skeleton.Avatar active size={20} shape="square" />
        </Flex>
      ),
    },
    {
      title: ' ',
      key: 'status',
      width: USERS_COLUMN_WIDTHS.status,
      render: () => (
        <Skeleton.Button
          active
          size="small"
          shape="round"
          style={{ width: 28, minWidth: 28, height: 16 }}
        />
      ),
    },
    {
      // The row-actions kebab: keeps the columns summing to the real table's width.
      title: ' ',
      key: 'actions',
      width: USERS_COLUMN_WIDTHS.actions,
      render: () => null,
    },
  ];

  return (
    /* The loading state wears the table box and its footer, so it does not
       jump a border when the data lands. */
    <div className="table-box">
      <Table<SkeletonRow>
        dataSource={dataSource}
        columns={columns}
        rowKey="key"
        pagination={false}
        scroll={{ x: USERS_TABLE_SCROLL_X }}
        aria-busy
      />
      <div className="table-footer">
        <Skeleton.Input active size="small" style={{ width: 150, height: 16 }} />
      </div>
    </div>
  );
}
